import { useEffect } from "react";
import { SlideContextProvider } from "./SlideContext";
import { SLIDE_HEIGHT, SLIDE_WIDTH } from "./SlideFrame";
import { DeckWordmark } from "./DeckWordmark";
import { slidesForPart } from "./print-parts";
import type { Deck, PrintPart } from "./types";

// One-slide-per-page print layout.
//
// Each slide renders at native 1280×960 dimensions. The @page rule matches so
// the browser's "Save as PDF" produces 17 PDF pages, one per slide.
//
// Critical detail: Tailwind's `md:` responsive classes use `@media (min-width:
// 768px)` which evaluates against the print viewport. Depending on the user's
// print-dialog page-size choice, that viewport can fall below 768px, in which
// case slides render in their mobile (single-column) layout instead of their
// desktop layout. The `forceDesktopLayoutForPrint` block below re-declares
// every `md:` class as unconditional inside `@media print`, with `!important`,
// scoped to `.print-slide`. The result: print always uses desktop layout,
// regardless of the browser viewport or paper size chosen in the print dialog.

const PRINT_PAGE_CSS = `
  @page {
    size: ${SLIDE_WIDTH}px ${SLIDE_HEIGHT}px;
    margin: 0;
  }
  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
    }
    /* Force browsers to print backgrounds, ring colors, and the blue tier
       pills exactly as on screen. Without this, Chromium/Safari strip
       background colors by default and slides like The Customer (slide 7)
       lose their grid color bands and pills. */
    .print-slide, .print-slide * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .print-controls {
      display: none !important;
    }
    .print-stack {
      gap: 0 !important;
      padding: 0 !important;
      background: white !important;
    }
    .print-slide {
      box-shadow: none !important;
      border-radius: 0 !important;
      break-after: page;
      page-break-after: always;
    }
    .print-slide:last-child {
      break-after: auto;
      page-break-after: auto;
    }
  }

  /* Force desktop layout in print, regardless of viewport.
     Re-declares every md:-prefixed Tailwind class used by the deck so the
     responsive @media gates collapse into unconditional rules. */
  @media print {
    /* Display */
    .print-slide .md\\:block { display: block !important; }
    .print-slide .md\\:grid { display: grid !important; }
    .print-slide .md\\:hidden { display: none !important; }
    .print-slide .md\\:flex-row { flex-direction: row !important; }
    .print-slide .md\\:flex-1 { flex: 1 1 0% !important; }
    .print-slide .md\\:h-full { height: 100% !important; }
    .print-slide .md\\:items-end { align-items: flex-end !important; }
    .print-slide .md\\:justify-between { justify-content: space-between !important; }
    .print-slide .md\\:justify-center { justify-content: center !important; }
    .print-slide .md\\:justify-end { justify-content: flex-end !important; }
    .print-slide .md\\:ml-auto { margin-left: auto !important; }
    .print-slide .md\\:mt-auto { margin-top: auto !important; }

    /* Grid */
    .print-slide .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
    .print-slide .md\\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
    .print-slide .md\\:grid-cols-5 { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; }
    .print-slide .md\\:grid-cols-\\[188px_1fr_1fr\\] { grid-template-columns: 188px 1fr 1fr !important; }
    .print-slide .md\\:grid-cols-\\[1fr_350px\\] { grid-template-columns: 1fr 350px !important; }
    .print-slide .md\\:grid-cols-\\[60px_1fr_280px\\] { grid-template-columns: 60px 1fr 280px !important; }
    .print-slide .md\\:grid-rows-\\[auto_minmax\\(0\\,1fr\\)_minmax\\(0\\,1fr\\)\\] { grid-template-rows: auto minmax(0,1fr) minmax(0,1fr) !important; }
    .print-slide .md\\:col-span-1 { grid-column: span 1 / span 1 !important; }

    /* Sizing */
    .print-slide .md\\:h-7 { height: 1.75rem !important; }
    .print-slide .md\\:h-10 { height: 2.5rem !important; }
    .print-slide .md\\:h-12 { height: 3rem !important; }
    .print-slide .md\\:h-24 { height: 6rem !important; }
    .print-slide .md\\:w-7 { width: 1.75rem !important; }
    .print-slide .md\\:w-10 { width: 2.5rem !important; }
    .print-slide .md\\:w-24 { width: 6rem !important; }
    .print-slide .md\\:w-auto { width: auto !important; }
    .print-slide .md\\:w-1\\/2 { width: 50% !important; }
    .print-slide .md\\:w-\\[250px\\] { width: 250px !important; }
    .print-slide .md\\:max-w-\\[800px\\] { max-width: 800px !important; }

    /* Padding */
    .print-slide .md\\:p-4 { padding: 1rem !important; }
    .print-slide .md\\:p-5 { padding: 1.25rem !important; }
    .print-slide .md\\:p-6 { padding: 1.5rem !important; }
    .print-slide .md\\:px-5 { padding-left: 1.25rem !important; padding-right: 1.25rem !important; }
    .print-slide .md\\:px-8 { padding-left: 2rem !important; padding-right: 2rem !important; }
    .print-slide .md\\:px-10 { padding-left: 2.5rem !important; padding-right: 2.5rem !important; }
    .print-slide .md\\:px-20 { padding-left: 5rem !important; padding-right: 5rem !important; }
    .print-slide .md\\:px-24 { padding-left: 6rem !important; padding-right: 6rem !important; }
    .print-slide .md\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
    .print-slide .md\\:py-5 { padding-top: 1.25rem !important; padding-bottom: 1.25rem !important; }
    .print-slide .md\\:py-6 { padding-top: 1.5rem !important; padding-bottom: 1.5rem !important; }
    .print-slide .md\\:py-7 { padding-top: 1.75rem !important; padding-bottom: 1.75rem !important; }
    .print-slide .md\\:py-10 { padding-top: 2.5rem !important; padding-bottom: 2.5rem !important; }
    .print-slide .md\\:py-16 { padding-top: 4rem !important; padding-bottom: 4rem !important; }
    .print-slide .md\\:py-20 { padding-top: 5rem !important; padding-bottom: 5rem !important; }
    .print-slide .md\\:pb-3 { padding-bottom: 0.75rem !important; }
    .print-slide .md\\:pt-5 { padding-top: 1.25rem !important; }
    .print-slide .md\\:pt-6 { padding-top: 1.5rem !important; }
    .print-slide .md\\:pt-8 { padding-top: 2rem !important; }
    .print-slide .md\\:pt-10 { padding-top: 2.5rem !important; }

    /* Margin */
    .print-slide .md\\:mb-6 { margin-bottom: 1.5rem !important; }
    .print-slide .md\\:mb-16 { margin-bottom: 4rem !important; }
    .print-slide .md\\:mt-1 { margin-top: 0.25rem !important; }
    .print-slide .md\\:mt-2 { margin-top: 0.5rem !important; }
    .print-slide .md\\:mt-3 { margin-top: 0.75rem !important; }
    .print-slide .md\\:mt-4 { margin-top: 1rem !important; }
    .print-slide .md\\:mt-5 { margin-top: 1.25rem !important; }
    .print-slide .md\\:mt-6 { margin-top: 1.5rem !important; }
    .print-slide .md\\:mt-7 { margin-top: 1.75rem !important; }
    .print-slide .md\\:mt-8 { margin-top: 2rem !important; }
    .print-slide .md\\:mt-10 { margin-top: 2.5rem !important; }
    .print-slide .md\\:-mt-8 { margin-top: -2rem !important; }
    .print-slide .md\\:-mt-10 { margin-top: -2.5rem !important; }

    /* Gap */
    .print-slide .md\\:gap-0 { gap: 0 !important; }
    .print-slide .md\\:gap-4 { gap: 1rem !important; }
    .print-slide .md\\:gap-5 { gap: 1.25rem !important; }
    .print-slide .md\\:gap-6 { gap: 1.5rem !important; }
    .print-slide .md\\:gap-7 { gap: 1.75rem !important; }
    .print-slide .md\\:gap-10 { gap: 2.5rem !important; }
    .print-slide .md\\:gap-12 { gap: 3rem !important; }
    .print-slide .md\\:gap-16 { gap: 4rem !important; }
    .print-slide .md\\:gap-x-6 { column-gap: 1.5rem !important; }

    /* Space-y (Tailwind generates :not(:last-child) margin-top selectors) */
    .print-slide .md\\:space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 1rem !important; }
    .print-slide .md\\:space-y-7 > :not([hidden]) ~ :not([hidden]) { margin-top: 1.75rem !important; }

    /* Text size */
    .print-slide .md\\:text-xs { font-size: 0.75rem !important; line-height: 1rem !important; }
    .print-slide .md\\:text-sm { font-size: 0.875rem !important; line-height: 1.25rem !important; }
    .print-slide .md\\:text-base { font-size: 1rem !important; line-height: 1.5rem !important; }
    .print-slide .md\\:text-lg { font-size: 1.125rem !important; line-height: 1.75rem !important; }
    .print-slide .md\\:text-xl { font-size: 1.25rem !important; line-height: 1.75rem !important; }
    .print-slide .md\\:text-2xl { font-size: 1.5rem !important; line-height: 2rem !important; }
    .print-slide .md\\:text-3xl { font-size: 1.875rem !important; line-height: 2.25rem !important; }
    .print-slide .md\\:text-4xl { font-size: 2.25rem !important; line-height: 2.5rem !important; }
    .print-slide .md\\:text-5xl { font-size: 3rem !important; line-height: 1 !important; }
    .print-slide .md\\:text-6xl { font-size: 3.75rem !important; line-height: 1 !important; }
    .print-slide .md\\:text-7xl { font-size: 4.5rem !important; line-height: 1 !important; }

    /* Text alignment + tracking + leading */
    .print-slide .md\\:text-right { text-align: right !important; }
    .print-slide .md\\:leading-\\[1\\.05\\] { line-height: 1.05 !important; }
    .print-slide .md\\:leading-\\[1\\.1\\] { line-height: 1.1 !important; }
    .print-slide .md\\:tracking-\\[0\\.2em\\] { letter-spacing: 0.2em !important; }
    .print-slide .md\\:tracking-\\[0\\.25em\\] { letter-spacing: 0.25em !important; }
  }
`;

export function PrintView({ deck, part }: { deck: Deck; part?: PrintPart }) {
  useEffect(() => {
    document.title = `${deck.title} — Print`;
  }, [deck.title]);

  return (
    <div className="min-h-screen bg-slate-200">
      <style>{PRINT_PAGE_CSS}</style>

      <div className="print-controls fixed right-4 top-4 z-50 flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700"
        >
          Print / Save PDF
        </button>
        <a
          href={window.location.pathname + (window.location.hash || "")}
          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-md ring-1 ring-slate-300 transition hover:bg-slate-50"
        >
          Back to deck
        </a>
      </div>

      <div className="print-stack flex flex-col items-center gap-6 bg-slate-200 p-6">
        {slidesForPart(deck.slides, part).map(({ slide, index: i }) => {
          const Slide = slide.Component;
          return (
            <div
              key={slide.id}
              className="print-slide relative overflow-hidden rounded-lg bg-white shadow-lg"
              style={{ width: SLIDE_WIDTH, height: SLIDE_HEIGHT }}
            >
              {i > 0 ? (
                <div className="absolute right-6 top-5 z-30">
                  <DeckWordmark />
                </div>
              ) : null}
              <SlideContextProvider
                index={i}
                total={deck.slides.length}
                slides={deck.slides}
              >
                <Slide
                  title={slide.title}
                  index={i}
                  total={deck.slides.length}
                />
              </SlideContextProvider>
            </div>
          );
        })}
      </div>
    </div>
  );
}
